import { FieldPath } from 'react-hook-form'
import { HouseConsignmentFormValues } from 'features/declaration/house-consignment/models/schema'
import { NctsCountry, tardekConfig } from '../../../../../../config'
import { isEmpty } from '../../../../../../shared/lib/isEmpty'
import { getValueByPath } from '../../../../../../shared/rhf/models/types'
import { FieldRequirement } from '../../../../models/fieldRules'
import { NctsError } from '../../../../../../shared/ncts/types'
import getDeclarationFieldRequirementsResolver from '../../fieldRequirements/registry'
import R0112Validator, { R0112_ERROR_MESSAGE } from '../../R0112'
import { collectFieldPaths } from '../collectFieldPaths'
import validateDocumentFileTypes from '../documentFileTypes'
import { FieldSateValidationContext } from '../types'

const validateFieldRequirements = (
  values: HouseConsignmentFormValues,
  fields: FieldPath<HouseConsignmentFormValues>[],
  getFieldRequirement: (path: string) => FieldRequirement,
  addError: (path: string, message: string, code?: string) => void,
  security: string,
) => {
  for (const path of fields) {
    const requirement = getFieldRequirement(path)
    const value = getValueByPath(values, path)
    if (requirement === 'required' && isEmpty(value)) {
      addError(path, 'Required')
    }

    if (R0112Validator.appliesTo(security)
      && path.endsWith('telephone')
      && typeof value === 'string' && !isEmpty(value)
      && !R0112Validator.isValidInternationalPhoneNumber(value)) {
      addError(path, R0112_ERROR_MESSAGE, 'R0112')
    }
  }
}

const validateFormFieldStates = ({
  isDraft,
  formValues,
  getFieldRequirement,
  security,
  country,
}: {
  isDraft: boolean
  formValues: HouseConsignmentFormValues
  getFieldRequirement: (path: string) => FieldRequirement
  security: string
  country: string
}) => {
  if (isDraft) {
    return []
  }

  const errors: NctsError[] = []

  const addError = (path: string, message: string, code?: string) => {
    errors.push({
      scope: 'houseConsignment',
      path,
      message,
      code,
    })
  }

  const allFields = collectFieldPaths(formValues) as FieldPath<HouseConsignmentFormValues>[]
  validateFieldRequirements(formValues, allFields, getFieldRequirement, addError, security)
  errors.push(...validateDocumentFileTypes(formValues, country, 'houseConsignment'))

  return errors
}

function validaHouseConsignmentFormFieldStates(
  formValues: HouseConsignmentFormValues,
  ctx: FieldSateValidationContext | undefined,
): NctsError[] {
  const country = ctx?.country ?? '' as NctsCountry
  const isDraft = ctx?.isDraft ?? true
  const isPhase6OptIn = tardekConfig.phase6OptInCountries.includes(country)
  const isPhase6OptOut = tardekConfig.phase6OptOutCountries.includes(country)
  const security = ctx?.security ?? '0'
  const phase = isPhase6OptIn || isPhase6OptOut ? 'p6' : 'p5'
  const additionalDeclarationType = ctx?.additionalDeclarationType ?? 'A'
  const specificCircumstanceIndicator = ctx?.specificCircumstanceIndicator ?? ''
  const isEns = ctx?.isEns ?? false

  const resolveRequirement = getDeclarationFieldRequirementsResolver('houseConsignment', isEns)

  return validateFormFieldStates({
    isDraft,
    formValues,
    security,
    country,
    getFieldRequirement: (path: string) => resolveRequirement(path, {
      country,
      phase,
      security,
      valuesMap: formValues,
      isOptIn: isPhase6OptIn,
      additionalDeclarationType,
      specificCircumstanceIndicator,
      serverValidationResult: ctx?.serverValidationResult,
    }),
  })
}

export default validaHouseConsignmentFormFieldStates
